import Poppins100 from "../static/fonts/Poppins-v20-100.woff2";
import Poppins200 from "../static/fonts/Poppins-v20-200.woff2";
import Poppins300 from "../static/fonts/Poppins-v20-300.woff2";
import Poppins400 from "../static/fonts/Poppins-v20-400.woff2";
import Poppins500 from "../static/fonts/Poppins-v20-500.woff2";
import Poppins600 from "../static/fonts/Poppins-v20-600.woff2";
import Poppins700 from "../static/fonts/Poppins-v20-700.woff2";
import { Colors, Theme } from "baseui/styles";
import { createTheme as baseCreateTheme, lightThemePrimitives } from "baseui";
import { LightTheme, Primitives } from "baseui/themes";

type TFontFaceSource = {
  family: string;
  src: string;
  style?: string;
  weight?: string;
};

export type TFontFaceSourceSet = Array<TFontFaceSource>;

export type PlatformTheme = Theme & {
  fontFaceSourceSet?: TFontFaceSourceSet;
  primaryFontFamily?: string;
  componentOverrides: {
    leftNav: {
      backgroundColor: string;
      textColor: string;
      hoverBackgroundColor: string;
      hoverTextColor: string;
      activeBackgroundColor: string;
      activeTextColor: string;
      borderSelected: string;
    };
    QuoteTabs: {
      activeBackgroundColor: string;
      activeTextColor: string;
      activeHoverBackgroundColor: string;
      activeHoverTextColor: string;
      hoverBackgroundColor?: string;
      inactiveBackgroundColor?: string;
      activeBorderColor?: string;
      inactiveBorderColor?: string;
      contentBorderColor?: string;
      textColor?: string;
    };
  };
  zIndex?: {
    modal: number;
    fixedComponent: number;
    popover: number;
  };
  colors?: {
    buttonPrimaryOutlinedFill: string;
    buttonPrimaryOutlinedText: string;
    buttonPrimaryOutlinedHoverFill: string;
    buttonPrimaryOutlinedHoverText: string;
    buttonPrimaryOutlinedDisabledText: string;
    buttonPrimaryOutlinedBorder: string;
    buttonSecondaryOutlinedText: string;
    buttonSecondaryOutlinedHoverFill: string;
    buttonSecondaryOutlinedHoverText: string;
    buttonSecondaryOutlinedDisabledText: string;
  } & Colors;
};

const loadFonts = (fontSet: TFontFaceSourceSet) => {
  fontSet.forEach((font: TFontFaceSource) => {
    const fontWeight = font.weight || "400";
    const fontStyle = font.style || "normal";
    const fontFace = new FontFace(font.family, `${font.src}`, {
      style: fontStyle,
      weight: fontWeight,
    });
    document.fonts.add(fontFace);
  });
};

export const useTheme = (): {
  getThemeWithOverrides: (overrides?: PlatformTheme) => Theme;
  fonts: TFontFaceSourceSet;
  theme: Theme;
  baseTheme: Theme;
} => {
  /**
   * Get (and load) a given font set definition dynamically.
   * We load fonts dynamically rather than statically as fonts may be overriden
   * by the backend at runtime.
   */
  const getFontsWithOverrides = (overrides?: PlatformTheme) => {
    const platformFontFaceSourceSet: TFontFaceSourceSet = [
      {
        family: "Poppins",
        src: `url(${Poppins100})`,
        weight: "100",
        style: "normal",
      },
      {
        family: "Poppins",
        src: `url(${Poppins200})`,
        weight: "200",
        style: "normal",
      },
      {
        family: "Poppins",
        src: `url(${Poppins300})`,
        weight: "300",
        style: "normal",
      },
      {
        family: "Poppins",
        src: `url(${Poppins400})`,
        weight: "400",
        style: "normal",
      },
      {
        family: "Poppins",
        src: `url(${Poppins500})`,
        weight: "500",
        style: "normal",
      },
      {
        family: "Poppins",
        src: `url(${Poppins600})`,
        weight: "600",
        style: "normal",
      },
      {
        family: "Poppins",
        src: `url(${Poppins700})`,
        weight: "700",
        style: "normal",
      },
    ];
    const fontFaceSourceSetOverrides =
      overrides?.fontFaceSourceSet || platformFontFaceSourceSet;

    loadFonts(fontFaceSourceSetOverrides);

    return fontFaceSourceSetOverrides;
  };

  const getThemeWithOverrides = (overrides?: PlatformTheme) => {
    const platformPrimaryFontFamily = "Poppins, sans-serif";
    const themePrimitives: Primitives = {
      ...lightThemePrimitives,
      primaryFontFamily: overrides?.primaryFontFamily || platformPrimaryFontFamily,
    };

    const themeOverrides: Partial<Theme> = {
      colors: {
        linkText: "#1C67F2",
        contentPrimary: "#282828",
        ...overrides?.colors,
      },
      grid: {
        ...LightTheme.grid,
        maxWidth: 1440,
        margins: [0, 36, 36],
        gutters: [0, 36, 36],
        unit: "px",
      },
    };
    const baseTheme = baseCreateTheme(themePrimitives, themeOverrides);
    getFontsWithOverrides(overrides);
    const themeWithOverrides: PlatformTheme = {
      ...baseTheme,
      colors: {
        ...baseTheme.colors,
        buttonPrimaryOutlinedFill: "transparent",
        buttonPrimaryOutlinedText: baseTheme.colors.contentPrimary,
        buttonPrimaryOutlinedHoverFill: baseTheme.colors.buttonPrimaryFill,
        buttonPrimaryOutlinedHoverText: baseTheme.colors.buttonPrimaryText,
        buttonPrimaryOutlinedDisabledText: baseTheme.colors.contentStateDisabled,
        buttonPrimaryOutlinedBorder: baseTheme.borders.border400.borderColor,
        buttonSecondaryOutlinedText: baseTheme.colors.contentSecondary,
        buttonSecondaryOutlinedDisabledText: baseTheme.colors.contentStateDisabled,
        ...overrides?.colors,
      },
      typography: {
        ...baseTheme.typography,
        HeadingXXLarge: {
          ...baseTheme.typography.HeadingXXLarge,
          fontSize: "24px",
          fontWeight: 700,
          lineHeight: "32px",
        },
        HeadingXLarge: {
          ...baseTheme.typography.HeadingXXLarge,
          fontSize: "18px",
          fontWeight: 700,
          lineHeight: "24px",
        },
      },
      componentOverrides: {
        leftNav: {
          backgroundColor: "#282828",
          textColor:
            overrides?.colors?.buttonPrimaryText || baseTheme.colors.buttonPrimaryText,
          hoverBackgroundColor: "rgba(255, 255, 255, 0.3)",
          hoverTextColor:
            overrides?.colors?.buttonPrimaryText || baseTheme.colors.buttonPrimaryText,
          activeBackgroundColor: "rgba(255, 255, 255, 0.3)",
          activeTextColor:
            overrides?.colors?.buttonPrimaryText || baseTheme.colors.buttonPrimaryText,
          borderSelected: "rgba(255, 255, 255, 0.5)",
          ...overrides?.componentOverrides?.leftNav,
        },
        QuoteTabs: {
          contentBorderColor: '"#878787"',
          inactiveBorderColor: "#878787",
          activeBorderColor: baseTheme.colors.buttonPrimaryFill,
          activeBackgroundColor: baseTheme.colors.buttonPrimaryFill,
          activeTextColor: baseTheme.colors.buttonPrimaryText,
          inactiveBackgroundColor: baseTheme.colors.backgroundPrimary,
          textColor: baseTheme.colors.contentPrimary,
          activeHoverBackgroundColor: baseTheme.colors.buttonPrimaryHover,
          activeHoverTextColor: baseTheme.colors.buttonPrimaryText,
          hoverBackgroundColor: baseTheme.colors.backgroundPrimary,
          ...overrides?.componentOverrides?.QuoteTabs,
        },
      },
      // zIndex is also defined in baseui theme, but we need to override it.
      zIndex: {
        fixedComponent: 1,
        modal: 2,
        popover: 3, // To make it work when a component inside a model opens a popover.
      },
    };
    return themeWithOverrides;
  };

  return {
    getThemeWithOverrides,
    fonts: getFontsWithOverrides(),
    theme: getThemeWithOverrides(),
    baseTheme: baseCreateTheme(lightThemePrimitives),
  };
};
